import { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { SHARE_MAX_SELECTIONS } from "@/components/ShareLinks";
import { PageHeader } from "@/components/PageHeader";
import { PlatformLogo } from "@/components/PlatformLogo";
import { PostedByToggle } from "@/components/PostedByToggle";
import { Skeleton } from "@/components/Skeleton";
import type {
  Client,
  ClientShareLink,
  ClientStatus,
  Deal,
  DealStage,
  HotOfferMatch,
  MatchedListing,
  PlacementMatch,
} from "@/types";
import { DEAL_STAGES } from "@/types";
import { BUILDING_STATUS_LABELS, CONDITION_LABELS } from "@/types";
import { NEIGHBORHOOD_LABELS } from "@/lib/neighborhoodVocab";

const STATUS_STYLES: Record<ClientStatus, string> = {
  active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  paused: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  closed: "border-slate-500/30 bg-slate-500/10 text-slate-400",
};

const PROPERTY_TYPE_LABEL: Record<string, string> = {
  apartment: "Apartment",
  house: "House",
  cottage: "Cottage",
  land: "Land",
  commercial: "Commercial",
};

const DEAL_TYPE_LABEL: Record<string, string> = {
  sale: "Sale",
  rent: "Rent",
  daily_rent: "Daily rent",
  mortgage: "Mortgage",
};

function formatDate(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}


function formatRange(min: string | null, max: string | null, suffix = "") {
  const mn = min ? Number(min) : null;
  const mx = max ? Number(max) : null;
  if (mn === null && mx === null) return null;
  const fmt = (n: number) => n.toLocaleString();
  if (mn !== null && mx !== null) return `${fmt(mn)}–${fmt(mx)}${suffix}`;
  if (mn !== null) return `≥ ${fmt(mn)}${suffix}`;
  return `≤ ${fmt(mx!)}${suffix}`;
}

// Score pill sits on top of a cover photo, so transparent tints wash out.
// Use a solid dark backdrop + saturated colored text/border for legibility
// against any image.
function scoreTone(score: number): string {
  const base = "font-bold bg-ink-950/85 backdrop-blur-sm shadow-sm shadow-black/40";
  if (score >= 80) return `${base} border-emerald-400/70 text-emerald-300`;
  if (score >= 60) return `${base} border-accent-400/70 text-accent-300`;
  if (score >= 40) return `${base} border-amber-400/70 text-amber-300`;
  return `${base} border-slate-400/50 text-slate-200`;
}

export function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const { user } = useAuth();

  const [client, setClient] = useState<Client | null>(null);
  const [clientErr, setClientErr] = useState<string | null>(null);

  const [placementMatches, setPlacementMatches] = useState<PlacementMatch[] | null>(null);
  const [hotMatches, setHotMatches] = useState<HotOfferMatch[] | null>(null);
  const [clientDeals, setClientDeals] = useState<Deal[] | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  // Whose listings to show in "From the market" — agents post a lot of
  // dupes that brokers filter out, and a private-only feed is often more
  // useful. Default 'any' keeps the legacy behavior on first load.
  const [postedBy, setPostedBy] = useState<"any" | "owner" | "agent">("any");
  const [rematching, setRematching] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  // ── prepare-for-client mode ──────────────────────────────────────────
  // Toggle that swaps each MatchCard into "selectable" mode so the broker
  // can pick a handful of offers to bundle into a public buyer page.
  // ``selected`` keys are ``placement:<id>`` or ``hot_offer:<id>`` so the
  // two match feeds share one selection set without ID collisions.
  const [prepareMode, setPrepareMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [shareLinks, setShareLinks] = useState<ClientShareLink[] | null>(null);
  const [generating, setGenerating] = useState(false);
  const [shareModal, setShareModal] = useState<ClientShareLink | null>(null);
  const [agentPhone, setAgentPhone] = useState("");
  // Default the public-page language to Georgian; the broker can flip to
  // RU/EN before generating when the client speaks one of those instead.
  const [shareLang, setShareLang] = useState<"ka" | "ru" | "en">("ka");

  const toggleSelected = useCallback((key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const exitPrepareMode = useCallback(() => {
    setPrepareMode(false);
    setSelected(new Set());
  }, []);

  const loadShareLinks = useCallback(async () => {
    if (!clientId) return;
    try {
      const r = await api<{ items: ClientShareLink[] }>(
        `/api/clients/${clientId}/share-links`,
      );
      setShareLinks(r.items);
    } catch {
      // Non-fatal; the rest of the page still works without this list.
      setShareLinks([]);
    }
  }, [clientId]);

  // Poll a freshly-created share link until photos finish rehosting.
  // We only run while the modal is open and the link is preparing — once
  // ``ready`` (or ``failed``) the polling stops on its own.
  useEffect(() => {
    if (!shareModal || shareModal.status !== "preparing" || !clientId) return;
    const token = shareModal.token;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api<{ items: ClientShareLink[] }>(
          `/api/clients/${clientId}/share-links`,
        );
        const fresh = r.items.find((l) => l.token === token);
        if (!cancelled && fresh) {
          setShareModal(fresh);
          setShareLinks(r.items);
        }
      } catch {
        // Ignore; the next tick will retry.
      }
    };
    const id = window.setInterval(tick, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [shareModal, clientId]);

  const loadClient = useCallback(async () => {
    if (!clientId) return;
    try {
      const c = await api<Client>(`/api/clients/${clientId}`);
      setClient(c);
    } catch (err) {
      setClientErr(
        err instanceof ApiError && err.status === 404
          ? "Client not found or you don't have access."
          : err instanceof Error
            ? err.message
            : "Failed to load client",
      );
    }
  }, [clientId]);

  const loadMatches = useCallback(
    async (withDismissed: boolean, posted: "any" | "owner" | "agent") => {
      if (!clientId) return;
      const params = new URLSearchParams({ limit: "200" });
      if (withDismissed) params.set("include_dismissed", "true");
      // posted_by only applies to the hot-offer feed; placement matches
      // are always the agency's own listings.
      const hotParams = new URLSearchParams(params);
      if (posted !== "any") hotParams.set("posted_by", posted);
      try {
        const [placements, offers] = await Promise.all([
          api<{ items: PlacementMatch[] }>(
            `/api/clients/${clientId}/placement-matches?${params.toString()}`,
          ),
          api<{ items: HotOfferMatch[] }>(
            `/api/clients/${clientId}/matches?${hotParams.toString()}`,
          ),
        ]);
        setPlacementMatches(placements.items);
        setHotMatches(offers.items);
      } catch (err) {
        setActionErr(err instanceof Error ? err.message : "Failed to load matches");
      }
    },
    [clientId],
  );

  const loadClientDeals = useCallback(async () => {
    if (!clientId) return;
    try {
      // Reuse the existing list endpoint with a client_id filter — no
      // dedicated route needed. ``limit=100`` is generous enough that
      // any realistic per-client funnel fits in one round-trip.
      const r = await api<{ items: Deal[] }>(
        `/api/deals?client_id=${clientId}&limit=100`,
      );
      setClientDeals(r.items);
    } catch {
      // Non-fatal — the panel just shows the empty state.
      setClientDeals([]);
    }
  }, [clientId]);

  useEffect(() => {
    loadClient();
    loadClientDeals();
  }, [loadClient, loadClientDeals]);

  useEffect(() => {
    loadMatches(showDismissed, postedBy);
  }, [loadMatches, showDismissed, postedBy]);

  useEffect(() => {
    loadShareLinks();
  }, [loadShareLinks]);

  async function generateShareLink() {
    if (!clientId) return;
    if (selected.size === 0) {
      setActionErr("Select at least one match before generating a public URL.");
      return;
    }
    const selections = Array.from(selected).map((k) => {
      const [kind, id] = k.split(":");
      return { kind: kind as "placement" | "hot_offer", id };
    });
    setGenerating(true);
    setActionErr(null);
    try {
      const link = await api<ClientShareLink>(
        `/api/clients/${clientId}/share-links`,
        {
          method: "POST",
          json: {
            selections,
            agent_phone: agentPhone.trim() || null,
            language: shareLang,
          },
        },
      );
      setShareModal(link);
      exitPrepareMode();
      await loadShareLinks();
    } catch (err) {
      setActionErr(
        err instanceof Error ? err.message : "Failed to generate public URL",
      );
    } finally {
      setGenerating(false);
    }
  }

  async function revokeShareLink(linkId: string) {
    if (!clientId) return;
    if (!window.confirm("Revoke this public URL? It will stop working immediately.")) {
      return;
    }
    try {
      await api(`/api/clients/${clientId}/share-links/${linkId}`, {
        method: "DELETE",
      });
      await loadShareLinks();
    } catch (err) {
      setActionErr(err instanceof Error ? err.message : "Revoke failed");
    }
  }

  async function rematch() {
    if (!clientId) return;
    // Synchronous flush — without this, React 18 batches these updates
    // with the ones that resolve after ``await`` below. On a fast backend
    // the skeleton never commits to the DOM and the user sees old cards
    // swap directly for new ones, which feels broken. ``flushSync`` forces
    // the "rematching / skeleton" state to paint before the network call
    // starts.
    flushSync(() => {
      setRematching(true);
      setActionErr(null);
      setPlacementMatches(null);
      setHotMatches(null);
    });
    // Floor the skeleton duration at ~450ms so users register progress even
    // when the backend rematch is very quick (~120ms). Without this the
    // skeleton flashes briefly and the UI looks like it didn't do anything.
    const minHoldMs = 450;
    const minHold = new Promise<void>((r) => setTimeout(r, minHoldMs));
    try {
      // Hot-offer matches (market pipeline) are computed inline — the POST
      // response carries the count directly. Placement matches run in the
      // background because the optional AI tiebreaker can add seconds;
      // we poll the placement-matches feed below until it populates.
      const res = await api<{
        client_id: string;
        hot_offer_matches: number;
        placement_matches_pending: boolean;
      }>(`/api/clients/${clientId}/rematch`, { method: "POST" });

      // Fetch the hot-offer feed once — matches are already in the DB by
      // the time the response arrived.
      const params = new URLSearchParams({ limit: "200" });
      if (showDismissed) params.set("include_dismissed", "true");
      const hotParams = new URLSearchParams(params);
      if (postedBy !== "any") hotParams.set("posted_by", postedBy);
      let hotItems: HotOfferMatch[] | null = null;
      try {
        const hot = await api<{ items: HotOfferMatch[] }>(
          `/api/clients/${clientId}/matches?${hotParams.toString()}`,
        );
        hotItems = hot.items;
      } catch {
        // Non-fatal; the count from the response is still displayed below.
      }
      // Ensure the floor elapses before we swap the skeleton for cards.
      await minHold;
      if (hotItems !== null) setHotMatches(hotItems);
      setActionErr(
        res.hot_offer_matches === 0
          ? "Hot-offer matching: 0 market listings fit the current preferences. Try relaxing filters (condition, building status, neighborhoods)."
          : null,
      );

      // Poll placement-matches until they land (Gemini may be in the loop).
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const r = await api<{ items: PlacementMatch[] }>(
          `/api/clients/${clientId}/placement-matches?${params.toString()}`,
        );
        setPlacementMatches(r.items);
        if (r.items.length > 0) break;
      }
    } catch (err) {
      setActionErr(err instanceof Error ? err.message : "Rematch failed");
    } finally {
      setRematching(false);
    }
  }

  async function dismissPlacementMatch(matchId: string) {
    if (!clientId) return;
    try {
      await api(`/api/clients/${clientId}/placement-matches/${matchId}/dismiss`, {
        method: "POST",
      });
      // Remove from the visible list (or flip its flag if viewing dismissed).
      setPlacementMatches((prev) =>
        prev
          ? showDismissed
            ? prev.map((m) => (m.id === matchId ? { ...m, dismissed: true } : m))
            : prev.filter((m) => m.id !== matchId)
          : prev,
      );
    } catch (err) {
      setActionErr(err instanceof Error ? err.message : "Dismiss failed");
    }
  }

  async function dismissHotMatch(matchId: string) {
    if (!clientId) return;
    try {
      await api(`/api/clients/${clientId}/matches/${matchId}/dismiss`, {
        method: "POST",
      });
      setHotMatches((prev) =>
        prev
          ? showDismissed
            ? prev.map((m) => (m.id === matchId ? { ...m, dismissed: true } : m))
            : prev.filter((m) => m.id !== matchId)
          : prev,
      );
    } catch (err) {
      setActionErr(err instanceof Error ? err.message : "Dismiss failed");
    }
  }

  const criteriaChips = useMemo(() => {
    if (!client) return [];
    const chips: { label: string; value: string }[] = [];
    if (client.deal_type)
      chips.push({ label: "Deal", value: DEAL_TYPE_LABEL[client.deal_type] || client.deal_type });
    if (client.rooms_min || client.rooms_max) {
      chips.push({
        label: "Rooms",
        value: formatRange(
          client.rooms_min != null ? String(client.rooms_min) : null,
          client.rooms_max != null ? String(client.rooms_max) : null,
        ) || "—",
      });
    }
    if (client.bedrooms_min || client.bedrooms_max) {
      chips.push({
        label: "Bedrooms",
        value: formatRange(
          client.bedrooms_min != null ? String(client.bedrooms_min) : null,
          client.bedrooms_max != null ? String(client.bedrooms_max) : null,
        ) || "—",
      });
    }
    if (client.floor_min != null || client.floor_max != null) {
      chips.push({
        label: "Floor",
        value: formatRange(
          client.floor_min != null ? String(client.floor_min) : null,
          client.floor_max != null ? String(client.floor_max) : null,
        ) || "—",
      });
    }
    const budget = formatRange(
      client.price_min,
      client.price_max,
      client.currency ? ` ${client.currency}` : "",
    );
    if (budget) chips.push({ label: "Budget", value: budget });
    const area = formatRange(client.area_min, client.area_max, " m²");
    if (area) chips.push({ label: "Area", value: area });
    if (client.property_types?.length)
      chips.push({
        label: "Type",
        value: client.property_types
          .map((t) => PROPERTY_TYPE_LABEL[t] || t)
          .join(", "),
      });
    if (client.districts?.length)
      chips.push({ label: "Districts", value: client.districts.join(", ") });
    if (client.building_statuses?.length)
      chips.push({
        label: "Building",
        value: client.building_statuses
          .map((s) => BUILDING_STATUS_LABELS[s] || s)
          .join(", "),
      });
    if (client.conditions?.length)
      chips.push({
        label: "Condition",
        value: client.conditions.map((c) => CONDITION_LABELS[c] || c).join(", "),
      });
    return chips;
  }, [client]);

  if (clientErr) {
    return (
      <div className="w-full">
        <PageHeader
          title="Client"
          subtitle={
            <Link to="/clients" className="text-accent-400 hover:text-accent-300">
              ← back to Clients
            </Link>
          }
        />
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {clientErr}
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="w-full space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-40" />
        <Skeleton className="h-60" />
      </div>
    );
  }

  const canBroker = user?.role === "executive_manager" || user?.role === "lead_broker";
  const assignedLabel =
    client.assigned_user?.full_name ||
    client.assigned_user?.email ||
    "— unassigned —";

  return (
    <div className="w-full space-y-5">
      <PageHeader
        title={client.full_name}
        subtitle={
          <Link to="/clients" className="text-accent-400 hover:text-accent-300">
            ← back to Clients
          </Link>
        }
        actions={
          <div className="flex items-center gap-2">
            <Link
              to={`/market?client=${clientId}`}
              className="btn-secondary"
              title="Search the full market with this client's preferences seeded"
            >
              🔎 Find for client
            </Link>
            {!prepareMode ? (
              <button
                type="button"
                onClick={() => setPrepareMode(true)}
                className="btn-secondary"
                title="Pick a few matches and create a public page for the client"
              >
                📤 Prepare for client
              </button>
            ) : (
              <button
                type="button"
                onClick={exitPrepareMode}
                className="btn-secondary"
                title="Exit selection mode"
              >
                ✕ Cancel selection
              </button>
            )}
            <button
              type="button"
              onClick={rematch}
              disabled={rematching}
              className="btn-secondary"
              title="Clear and recompute AI matches against current listings"
            >
              {rematching ? "Rematching…" : "✨ Rematch"}
            </button>
          </div>
        }
      />

      {actionErr && (
        <div className="card p-3 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {actionErr}
        </div>
      )}

      {/* Client summary */}
      <div className="card p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`badge ${STATUS_STYLES[client.status]}`}>
                {client.status}
              </span>
              {canBroker && (
                <span className="text-[11px] text-slate-500">
                  <span className="uppercase tracking-wider text-slate-600 font-semibold mr-1">
                    Broker
                  </span>
                  {assignedLabel}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-300 flex-wrap">
              {client.phone && (
                <a
                  href={`tel:${client.phone}`}
                  className="mono hover:text-accent-300"
                >
                  {client.phone}
                </a>
              )}
              {client.email && (
                <a
                  href={`mailto:${client.email}`}
                  className="hover:text-accent-300"
                >
                  {client.email}
                </a>
              )}
              {client.source && (
                <span className="text-slate-500">
                  via <span className="text-slate-400">{client.source}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {criteriaChips.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {criteriaChips.map((c) => (
              <div
                key={c.label}
                className="inline-flex items-baseline gap-1.5 rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-1.5 text-xs"
              >
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  {c.label}
                </span>
                <span className="text-slate-200">{c.value}</span>
              </div>
            ))}
          </div>
        )}

        {client.notes && (
          <div className="mt-4 rounded-md border border-white/5 bg-white/[0.02] p-3 text-sm text-slate-300 whitespace-pre-wrap">
            {client.notes}
          </div>
        )}
      </div>

      {/* Active share links — surfaced above the matches so the broker can
          re-copy a link or revoke without scrolling. Hidden when there are
          none to keep the page clean. */}
      {shareLinks && shareLinks.length > 0 && (
        <div className="card p-4">
          <div className="flex items-baseline gap-2 mb-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-accent-400">
              Public links for this client
            </h3>
            <span className="text-xs text-slate-500">
              {shareLinks.length} active
            </span>
          </div>
          <ul className="divide-y divide-white/5">
            {shareLinks.map((link) => (
              <ShareLinkRow
                key={link.id}
                link={link}
                onCopy={() => copyToClipboard(absolutePublicUrl(link.public_url))}
                onRevoke={() => revokeShareLink(link.id)}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Prepare-mode banner — instructs the broker, shows live count, and
          provides the primary "Generate public URL" CTA in one place. */}
      {prepareMode && (
        <div className="card p-4 border-accent-500/30 bg-accent-500/[0.04] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="text-sm font-semibold text-accent-200 mb-1">
              Pick the offers you want to share
            </div>
            <div className="text-xs text-slate-400">
              Selected matches become a polished public page with the client.
              We rehost the photos and put your contact details on every card.
              Up to {SHARE_MAX_SELECTIONS} per link.
            </div>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <input
                type="tel"
                value={agentPhone}
                onChange={(e) => setAgentPhone(e.target.value)}
                placeholder="Your phone for the contact card (optional)"
                className="input text-xs w-72"
                maxLength={32}
              />
              {/* Public-page language. Defaults to Georgian; flip to RU/EN
                  if the client speaks one of those. The buyer can also
                  override on the page itself, but most won't bother — set
                  the right default and conversion gets easier. */}
              <div className="inline-flex items-center gap-1.5 text-xs">
                <span className="text-slate-500">Language:</span>
                <div
                  role="radiogroup"
                  aria-label="Public page language"
                  className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-0.5"
                >
                  {([
                    { code: "ka" as const, label: "GE", flag: "🇬🇪", title: "ქართული" },
                    { code: "ru" as const, label: "RU", flag: "🇷🇺", title: "Русский" },
                    { code: "en" as const, label: "EN", flag: "🇬🇧", title: "English" },
                  ]).map(({ code, label, flag, title }) => {
                    const active = shareLang === code;
                    return (
                      <button
                        key={code}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        title={title}
                        onClick={() => setShareLang(code)}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold leading-none transition ${
                          active
                            ? "bg-white/15 text-white"
                            : "text-slate-400 hover:text-slate-200"
                        }`}
                      >
                        <span aria-hidden>{flag}</span>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-300">
              <span className="text-accent-300 font-semibold tabular-nums">
                {selected.size}
              </span>{" "}
              selected
            </span>
            <button
              type="button"
              onClick={generateShareLink}
              disabled={generating || selected.size === 0}
              className="btn-primary"
            >
              {generating ? "Generating…" : "🔗 Generate public URL"}
            </button>
            <button
              type="button"
              onClick={exitPrepareMode}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Deals for this client */}
      <ClientDealsPanel
        deals={clientDeals}
        clientId={clientId || ""}
        clientName={client.full_name}
      />

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-400">
          Listings matched to this client's preferences, ranked by fit score.
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <PostedByToggle value={postedBy} onChange={setPostedBy} />
          <label className="inline-flex items-center gap-2 text-xs text-slate-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showDismissed}
              onChange={(e) => setShowDismissed(e.target.checked)}
              className="accent-accent-500"
            />
            Show dismissed
          </label>
        </div>
      </div>

      {/* Placement matches — your agency's own listings (AI-scored) */}
      <section>
        <div className="flex items-baseline gap-3 mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-accent-400">
            From your listings
          </h2>
          <span className="text-xs text-slate-500">
            {placementMatches === null
              ? "loading…"
              : `${placementMatches.length} match${placementMatches.length === 1 ? "" : "es"} · AI-scored`}
          </span>
        </div>
        {placementMatches === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 [grid-auto-rows:1fr]">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-72" />
            ))}
          </div>
        ) : placementMatches.length === 0 ? (
          <div className="card p-5 text-sm text-slate-500">
            {rematching
              ? "Scanning your published listings with Gemini…"
              : "No placement matches yet. Rematch to rescore against current listings."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 [grid-auto-rows:1fr]">
            {placementMatches.map((m) => {
              const key = `placement:${m.placement.id}`;
              return (
                <MatchCard
                  key={m.id}
                  listing={m.placement}
                  score={Number(m.score)}
                  aiReason={m.ai_reason}
                  dismissed={m.dismissed}
                  createdAt={m.created_at}
                  onDismiss={() => dismissPlacementMatch(m.id)}
                  variant="placement"
                  selectable={prepareMode}
                  selected={selected.has(key)}
                  onToggleSelect={() => toggleSelected(key)}
                />
              );
            })}
          </div>
        )}
      </section>

      {/* Market matches — listings scraped from the portals, filtered through
          the same pipeline as the /market page, ranked by deal quality. */}
      <section>
        <div className="flex items-baseline gap-3 mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-300">
            From the market
          </h2>
          <span className="text-xs text-slate-500">
            {rematching && hotMatches === null
              ? "searching market…"
              : hotMatches === null
                ? "loading…"
                : `${hotMatches.length} match${hotMatches.length === 1 ? "" : "es"} · market-filtered, ranked by deal quality`}
          </span>
        </div>
        {hotMatches === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 [grid-auto-rows:1fr]">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-72" />
            ))}
          </div>
        ) : hotMatches.length === 0 ? (
          <div className="card p-5 text-sm text-slate-500">
            {rematching
              ? "Searching the market through this client's filters…"
              : "No market listings fit the current preferences. Try loosening condition, building status, or neighborhoods."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 [grid-auto-rows:1fr]">
            {hotMatches.map((m) => {
              const key = `hot_offer:${m.offer.id}`;
              return (
                <MatchCard
                  key={m.id}
                  listing={m.offer}
                  score={Number(m.score)}
                  aiReason={m.ai_reason}
                  dismissed={m.dismissed}
                  createdAt={m.created_at}
                  onDismiss={() => dismissHotMatch(m.id)}
                  variant="offer"
                  selectable={prepareMode}
                  selected={selected.has(key)}
                  onToggleSelect={() => toggleSelected(key)}
                />
              );
            })}
          </div>
        )}
      </section>

      {shareModal && (
        <ShareLinkModal
          link={shareModal}
          onClose={() => setShareModal(null)}
          onCopy={() => copyToClipboard(absolutePublicUrl(shareModal.public_url))}
        />
      )}
    </div>
  );
}

function ShareLinkRow({
  link,
  onCopy,
  onRevoke,
}: {
  link: ClientShareLink;
  onCopy: () => void;
  onRevoke: () => void;
}) {
  const url = absolutePublicUrl(link.public_url);
  const expiresLabel = formatDate(link.expires_at);
  const statusTone =
    link.status === "ready"
      ? "text-emerald-300"
      : link.status === "preparing"
        ? "text-amber-300"
        : "text-rose-300";
  return (
    <li className="py-2 flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-[200px]">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-accent-300 hover:text-accent-200 mono break-all"
        >
          {url}
        </a>
        <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
          <span className={statusTone}>{link.status}</span>
          <span>·</span>
          <span>{link.item_count} offers</span>
          <span>·</span>
          <span title="Public-page language">
            {link.language === "ru" ? "🇷🇺 RU" : link.language === "en" ? "🇬🇧 EN" : "🇬🇪 GE"}
          </span>
          <span>·</span>
          <span>expires {expiresLabel}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" className="btn-secondary text-xs" onClick={onCopy}>
          Copy
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary text-xs"
        >
          Open
        </a>
        <button
          type="button"
          className="text-xs text-slate-500 hover:text-rose-300"
          onClick={onRevoke}
        >
          Revoke
        </button>
      </div>
    </li>
  );
}

function ShareLinkModal({
  link,
  onClose,
  onCopy,
}: {
  link: ClientShareLink;
  onClose: () => void;
  onCopy: () => void;
}) {
  const url = absolutePublicUrl(link.public_url);
  const preparing = link.status === "preparing";
  const failed = link.status === "failed";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="card max-w-lg w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="text-lg font-semibold text-slate-100">
            Public URL ready to share
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Send this link to the client. They'll see a clean page with the
            offers you picked, your contact details, and high-res photos. The
            link expires {formatDate(link.expires_at)}.
          </p>
        </div>
        <div className="flex items-stretch gap-2">
          <input
            type="text"
            readOnly
            value={url}
            onClick={(e) => (e.target as HTMLInputElement).select()}
            className="input flex-1 mono text-xs"
          />
          <button type="button" className="btn-primary" onClick={onCopy}>
            Copy
          </button>
        </div>
        {preparing && (
          <div className="text-xs text-amber-300 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            Photos still rehosting in the background — the link works the
            moment it flips to ready ({link.items.filter((i) => i.status === "ready").length}/
            {link.items.length} items rehosted).
          </div>
        )}
        {failed && (
          <div className="text-xs text-rose-300">
            Photo rehosting failed for every selected offer. Try generating
            again, or pick different listings.
          </div>
        )}
        {!preparing && !failed && (
          <div className="text-xs text-emerald-300">
            ✓ Ready — all {link.item_count} offers are live on the public page.
          </div>
        )}
        <div className="flex justify-end">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function absolutePublicUrl(path: string): string {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (typeof window !== "undefined") {
    return `${window.location.origin}${path}`;
  }
  return path;
}

function copyToClipboard(value: string) {
  if (!value) return;
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(value).catch(() => {
      // Fallback to legacy textarea-select-execCommand path; clipboard
      // permissions are restricted in some embedded contexts (iframes).
      _legacyCopy(value);
    });
    return;
  }
  _legacyCopy(value);
}

function _legacyCopy(value: string) {
  const el = document.createElement("textarea");
  el.value = value;
  el.setAttribute("readonly", "");
  el.style.position = "absolute";
  el.style.left = "-9999px";
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand("copy");
  } catch {
    /* swallow — copy is best-effort */
  }
  document.body.removeChild(el);
}

function MatchCard({
  listing,
  score,
  aiReason,
  dismissed,
  createdAt,
  onDismiss,
  variant,
  selectable = false,
  selected = false,
  onToggleSelect,
}: {
  listing: MatchedListing;
  score: number;
  aiReason: string | null;
  dismissed: boolean;
  createdAt: string;
  onDismiss: () => void;
  variant: "placement" | "offer";
  /** When true, the cover-photo click toggles selection instead of opening
   *  the source URL, and a checkbox overlay appears on the card. */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}) {
  // Mirrors the MarketCard layout so brokers see the same information shape
  // whether they're browsing /market or their client's matches — the only
  // differences are the match-specific overlays (score badge, dismiss,
  // AI reason) and the fact that placements don't have benchmark/VIP data.
  const rounded = Math.round(score);
  const priceUsd = listing.price_usd ? formatUsdStr(listing.price_usd) : null;
  const priceNative = formatPriceNative(listing.price, listing.currency);
  const showNativeSecondary =
    priceNative !== null && (!priceUsd || listing.currency !== "USD");

  const dealLabel = listing.deal_type
    ? DEAL_TYPE_LABEL[listing.deal_type] || listing.deal_type
    : null;
  const typeLabel = listing.property_type
    ? PROPERTY_TYPE_LABEL[listing.property_type] || listing.property_type
    : null;
  const areaText = listing.area_sqm
    ? `${Number(listing.area_sqm).toLocaleString(undefined, { maximumFractionDigits: 0 })} m²`
    : null;
  const floorText =
    listing.floor != null
      ? listing.total_floors
        ? `${listing.floor}/${listing.total_floors}`
        : `${listing.floor}`
      : null;

  // Prefer the immutable first_seen_at fallback over last_seen_at — the
  // latter gets bumped to now() on every revisit, which would falsely
  // report old re-scraped listings as freshly posted.
  const freshnessAt =
    listing.listed_at || listing.published_at || listing.first_seen_at || listing.last_seen_at || createdAt;

  const hoverBorder = listing.welcomes_agents
    ? "border-amber-400/40 shadow-[0_0_0_1px_rgba(251,191,36,0.15),0_0_32px_-8px_rgba(251,191,36,0.35)] hover:border-amber-400/60"
    : variant === "placement"
      ? "hover:border-accent-500/40"
      : "hover:border-accent-500/30";

  // Selection-mode swaps the cover-photo behavior: click toggles the
  // checkbox instead of opening the source listing in a new tab. The
  // visual treatment ramps the border + drops a checkmark overlay so
  // the user can scan which cards they've picked at a glance.
  const selectionBorder = selectable
    ? selected
      ? "border-accent-400 shadow-[0_0_0_2px_rgba(99,102,241,0.45)]"
      : "border-white/10"
    : "";
  const handleCoverClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (selectable) {
      e.preventDefault();
      onToggleSelect?.();
    }
  };

  return (
    <div
      className={`card overflow-hidden flex flex-col group/card h-full transition ${hoverBorder} ${selectionBorder} ${
        dismissed ? "opacity-60" : ""
      }`}
    >
      <a
        href={listing.published_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleCoverClick}
        className={`relative block aspect-[4/3] bg-white/[0.03] overflow-hidden ${
          selectable ? "cursor-pointer" : ""
        }`}
      >
        {listing.cover_photo_url ? (
          <img
            src={listing.cover_photo_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-300"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-700 text-xs">
            no photo
          </div>
        )}

        {/* Top-left: platform logo + welcomes-agents highlight (owner
            explicitly signals OK-with-agencies) — same rail as the market card.
            In selection mode we lead with the checkbox so it's the first
            thing the broker's eye lands on. */}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {selectable && (
            <span
              className={`flex items-center justify-center w-6 h-6 rounded-md border-2 transition ${
                selected
                  ? "bg-accent-500 border-accent-300 text-white"
                  : "bg-ink-950/70 border-white/40 text-transparent backdrop-blur-sm"
              }`}
              aria-label={selected ? "Deselect" : "Select"}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
          )}
          <PlatformLogo platform={listing.platform} size="sm" />
          {listing.welcomes_agents && (
            <span
              className="badge text-[10px] border-amber-400/60 bg-black/65 text-amber-200 shadow-md backdrop-blur-sm"
              title="Owner explicitly welcomes agencies — priority lead"
            >
              🔥 Hot
            </span>
          )}
        </div>

        {/* Top-right: dismissed pill + match score. Score is the card's most
            important UI signal so it stays visually prominent. */}
        <div className="absolute top-2 right-2 flex items-center gap-1.5">
          {dismissed && (
            <span className="badge text-[10px] border-rose-400/60 bg-black/70 text-rose-200 shadow-md backdrop-blur-sm">
              dismissed
            </span>
          )}
          <span className={`badge text-[11px] px-2 py-1 shadow-md backdrop-blur-sm ${scoreTone(score)}`}>
            {rounded}%
          </span>
        </div>

        {/* Bottom-left: deal type + provenance badges. Agency/exclusive/forbids
            flags tell the broker how negotiable this lead actually is before
            they click through. */}
        <div className="absolute bottom-2 left-2 flex items-center gap-1.5 flex-wrap max-w-[80%]">
          {dealLabel && (
            <span
              className={`badge text-[10px] bg-black/65 shadow-md backdrop-blur-sm ${
                listing.deal_type === "sale"
                  ? "border-accent-500/60 text-accent-200"
                  : "border-emerald-500/60 text-emerald-200"
              }`}
            >
              {dealLabel}
            </span>
          )}
          {listing.owner_type === "agency" && (
            <span
              className="badge text-[10px] border-white/30 bg-black/70 text-slate-100 shadow-md backdrop-blur-sm"
              title="Posted by an agency"
            >
              agency
            </span>
          )}
          {listing.is_exclusive && (
            <span
              className="badge text-[10px] border-rose-400/60 bg-black/70 text-rose-200 shadow-md backdrop-blur-sm"
              title="Exclusive to another agency — can't republish"
            >
              exclusive
            </span>
          )}
          {listing.forbids_agents && (
            <span
              className="badge text-[10px] border-amber-400/60 bg-black/70 text-amber-200 shadow-md backdrop-blur-sm"
              title="Owner explicitly asked for no agencies"
            >
              ⚠ no agents
            </span>
          )}
        </div>

        {/* Bottom-right: VIP level (portal-promoted) + photo count. */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
          {listing.vip_level && (
            <span
              className="badge text-[10px] border-yellow-400/60 bg-black/70 text-yellow-200 shadow-md backdrop-blur-sm"
              title="Portal-promoted listing"
            >
              {listing.vip_level.replace("_", " ")}
            </span>
          )}
          {listing.photo_count ? (
            <span className="badge text-[10px] border-white/20 bg-black/70 text-slate-100 shadow-md backdrop-blur-sm">
              📷 {listing.photo_count}
            </span>
          ) : null}
        </div>
      </a>

      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-base font-semibold text-slate-100 tabular-nums">
              {priceUsd || priceNative || <span className="text-slate-500">—</span>}
            </div>
            {showNativeSecondary && priceNative && priceUsd && (
              <div className="text-[11px] text-slate-500 tabular-nums">{priceNative}</div>
            )}
            <PricePositionChipInline listing={listing} />
          </div>
          <div
            className="text-[11px] text-slate-500 whitespace-nowrap"
            title={formatDate(freshnessAt)}
          >
            {timeAgo(freshnessAt)}
          </div>
        </div>

        {listing.title && (
          <h3
            className="text-[13px] leading-snug font-medium text-slate-200 line-clamp-2"
            title={listing.title}
          >
            {listing.title}
          </h3>
        )}

        {(typeLabel || listing.rooms || listing.bedrooms || areaText || floorText) && (
          <div className="text-[12px] text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1">
            {typeLabel && <span>{typeLabel}</span>}
            {listing.rooms != null && (
              <span className="inline-flex items-center gap-1" title={`${listing.rooms} rooms total`}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18" />
                  <path d="M3 22h18" />
                  <circle cx="14" cy="13" r="0.6" fill="currentColor" />
                </svg>
                {listing.rooms}
              </span>
            )}
            {listing.bedrooms != null && listing.bedrooms > 0 && (
              <span
                className="inline-flex items-center gap-1"
                title={`${listing.bedrooms} bedroom${listing.bedrooms > 1 ? "s" : ""}`}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 4v16" />
                  <path d="M2 8h18a2 2 0 0 1 2 2v10" />
                  <path d="M2 17h20" />
                  <path d="M6 8v9" />
                </svg>
                {listing.bedrooms}
              </span>
            )}
            {areaText && (
              <span className="inline-flex items-center gap-1" title="Area">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h4M3 15h4M9 3v4M15 3v4" />
                </svg>
                {areaText}
              </span>
            )}
            {floorText && (
              <span
                className="inline-flex items-center gap-1"
                title={listing.total_floors ? `Floor ${listing.floor} of ${listing.total_floors}` : `Floor ${listing.floor}`}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2 2 7l10 5 10-5-10-5z" />
                  <path d="M2 12l10 5 10-5" />
                  <path d="M2 17l10 5 10-5" />
                </svg>
                {floorText}
              </span>
            )}
          </div>
        )}

        {(listing.neighborhood || listing.district) && (
          <div className="text-[12px] text-slate-500 flex items-center gap-1">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {listing.neighborhood ? (
              <span>
                <span className="text-slate-300">
                  {NEIGHBORHOOD_LABELS[listing.neighborhood] ?? listing.neighborhood}
                </span>
                {listing.district && (
                  <span className="text-slate-600"> · {listing.district}</span>
                )}
              </span>
            ) : (
              listing.district
            )}
          </div>
        )}

        {/* AI reason for placement matches; short_description for hot offers. */}
        {variant === "placement" && aiReason ? (
          <div className="text-[12px] leading-relaxed text-accent-200/90 bg-accent-500/5 border border-accent-500/15 rounded-md px-2.5 py-2 line-clamp-3">
            <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-accent-400 mr-1.5">
              AI
            </span>
            {aiReason}
          </div>
        ) : variant === "offer" && listing.short_description ? (
          <div className="text-[12px] text-slate-400 line-clamp-3 leading-relaxed">
            {listing.short_description}
          </div>
        ) : null}

        <div className="mt-auto pt-2 flex items-center justify-between gap-2 text-[11px] text-slate-500 border-t border-white/[0.04]">
          <a
            href={listing.published_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-400 hover:text-accent-300 font-medium"
          >
            Open ↗
          </a>
          {!dismissed && (
            <button
              type="button"
              onClick={onDismiss}
              className="text-slate-500 hover:text-rose-300"
              title="Dismiss this match"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** USD price formatter — the Market page has one called `formatUsd`; we
 *  inline the same logic here to avoid importing a private helper. */
function formatUsdStr(v: string | null): string | null {
  if (!v) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return "$" + n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** Native-currency secondary price for GEL-listed offers or cross-checks. */
function formatPriceNative(price: string | null | undefined, currency: string | null | undefined): string | null {
  if (!price) return null;
  const n = Number(price);
  if (!Number.isFinite(n)) return price;
  const sym =
    currency === "USD" ? "$"
    : currency === "GEL" ? "₾"
    : currency === "EUR" ? "€"
    : currency ? currency + " "
    : "";
  return `${sym}${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/** Price-position chip (cheap/average/expensive vs cohort median). Mirrors
 *  the Market page's ``PricePositionChip`` — kept local because the Market
 *  version is tightly coupled to its ``MarketListing`` type, and the
 *  MatchedListing type has the same underlying fields. */
function PricePositionChipInline({ listing: o }: { listing: MatchedListing }) {
  if (!o.price_position || !o.price_per_sqm_usd) return null;
  const pct = o.price_percent_vs_median;
  const median = o.neighborhood_median_sqm_usd;
  const pps = Number(o.price_per_sqm_usd);
  if (median == null || pct == null) return null;

  const styles = {
    cheap: { classes: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25", icon: "↓", verb: "below" },
    average: { classes: "bg-slate-500/10 text-slate-400 border-slate-500/20", icon: "≈", verb: "vs" },
    expensive: { classes: "bg-rose-500/15 text-rose-300 border-rose-500/25", icon: "↑", verb: "above" },
  }[o.price_position];

  const shortLabel =
    o.price_position === "average"
      ? "At neighborhood median"
      : `${Math.abs(pct).toFixed(0)}% ${styles.verb} median`;

  const sample = o.neighborhood_sample_size ?? 0;
  // Dim slightly for lower-precision benchmark tiers — same treatment as
  // the Market card (L1 full-precision = bright, L2/L3 = dimmer chip).
  const isLowTier = o.price_benchmark_tier !== "l1_full";
  const dimClass = isLowTier ? "opacity-70" : "";

  return (
    <div
      className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${styles.classes} ${dimClass}`}
      title={`${pps.toLocaleString(undefined, { maximumFractionDigits: 0 })} $/m² vs cohort median ${median.toLocaleString(undefined, { maximumFractionDigits: 0 })} $/m² (n=${sample})`}
    >
      <span>{styles.icon}</span>
      <span>{shortLabel}</span>
    </div>
  );
}

/** Compact "matched 3h ago" style timestamp, mirroring HotOffers' timeAgo. */
function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
}

const DEAL_STAGE_META: Record<
  DealStage,
  { label: string; color: string; accent: string }
> = {
  planning: {
    label: "Planning",
    color: "border-blue-500/30 bg-blue-500/10",
    accent: "text-blue-300",
  },
  booked: {
    label: "Booked",
    color: "border-cyan-500/30 bg-cyan-500/10",
    accent: "text-cyan-300",
  },
  decision_pending: {
    label: "Decision pending",
    color: "border-amber-500/30 bg-amber-500/10",
    accent: "text-amber-300",
  },
  done: {
    label: "Done",
    color: "border-emerald-500/30 bg-emerald-500/10",
    accent: "text-emerald-300",
  },
  lost: {
    label: "Lost",
    color: "border-rose-500/30 bg-rose-500/10",
    accent: "text-rose-300",
  },
};

/** Compact deals panel for the client-detail page. Shows stage tally
 *  pills, a list of the client's deals (with stage badge + price +
 *  link into the Deals page), and a "+ New deal" CTA that hops over
 *  to /deals with the client preselected. */
function ClientDealsPanel({
  deals,
  clientId,
  clientName,
}: {
  deals: Deal[] | null;
  clientId: string;
  clientName: string;
}) {
  // Order by created_at desc — most recent first.
  const sorted = useMemo(() => {
    if (!deals) return null;
    return [...deals].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [deals]);

  // Stage tally — used for the pill summary at the top of the panel.
  const counts = useMemo(() => {
    const m = new Map<DealStage, number>();
    for (const stage of DEAL_STAGES) m.set(stage, 0);
    for (const d of deals ?? []) {
      m.set(d.stage, (m.get(d.stage) ?? 0) + 1);
    }
    return m;
  }, [deals]);

  const hasAny = sorted && sorted.length > 0;
  const inPipeline =
    (counts.get("booked") ?? 0) + (counts.get("decision_pending") ?? 0);
  const won = counts.get("done") ?? 0;

  return (
    <section className="card p-4 space-y-3">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-accent-400">
          Deals
        </h2>
        <div className="text-xs text-slate-500">
          {sorted === null
            ? "loading…"
            : sorted.length === 0
              ? "no deals yet"
              : `${sorted.length} total · ${inPipeline} in pipeline · ${won} won`}
        </div>
        <Link
          to={`/deals?newClient=${encodeURIComponent(clientId)}`}
          className="btn-primary !px-3 !py-1.5 !text-xs ml-auto"
          title={`Start a new deal with ${clientName}`}
        >
          + New deal
        </Link>
      </div>

      {sorted === null && <Skeleton className="h-16" />}

      {hasAny && (
        <>
          {/* Stage tally — small color-coded pills, hides empty stages */}
          <div className="flex flex-wrap gap-1.5">
            {DEAL_STAGES.map((stage) => {
              const n = counts.get(stage) ?? 0;
              if (n === 0) return null;
              const meta = DEAL_STAGE_META[stage];
              return (
                <Link
                  key={stage}
                  to="/deals"
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta.color} ${meta.accent} hover:bg-white/[0.04]`}
                >
                  <span>{meta.label}</span>
                  <span className="tabular-nums opacity-80">{n}</span>
                </Link>
              );
            })}
          </div>

          {/* Compact list — title, price, stage badge, link into /deals */}
          <ul className="divide-y divide-white/5">
            {sorted.map((d) => {
              const meta = DEAL_STAGE_META[d.stage];
              return (
                <li
                  key={d.id}
                  className="flex items-center gap-3 py-2.5 first:pt-1.5"
                >
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border whitespace-nowrap ${meta.color} ${meta.accent}`}
                  >
                    {meta.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-100 truncate">
                      {d.property_title || (
                        <span className="text-slate-500 italic">
                          (untitled)
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {d.price && (
                        <span className="tabular-nums">
                          {Number(d.price).toLocaleString()}
                          {d.currency ? ` ${d.currency}` : ""}
                        </span>
                      )}
                      {d.assigned_user && (
                        <span className="text-slate-600">
                          {d.price ? " · " : ""}
                          {d.assigned_user.full_name ||
                            d.assigned_user.email ||
                            "—"}
                        </span>
                      )}
                      <span className="text-slate-600">
                        {" · "}
                        {timeAgo(d.created_at)}
                      </span>
                    </div>
                  </div>
                  <Link
                    to="/deals"
                    className="text-[11px] text-accent-400 hover:text-accent-300 whitespace-nowrap"
                  >
                    Open ↗
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {sorted !== null && sorted.length === 0 && (
        <div className="text-[12px] text-slate-500">
          No deals for this client yet. Use{" "}
          <span className="text-slate-300 font-medium">+ New deal</span> to
          start one.
        </div>
      )}
    </section>
  );
}
